import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import {
  DASH,
  displayValue,
  formatDate,
  sortByDateDesc,
} from "../utils/equipmentPassportConfig";

function PassportState({ title, text, tone = "muted" }) {
  return (
    <Layout>
      <div className="container">
        <div className={`passport-state passport-state--${tone}`}>
          <h1>{title}</h1>
          {text && <p>{text}</p>}
        </div>
      </div>
    </Layout>
  );
}

function FieldGrid({ fields }) {
  return (
    <dl className="passport-field-grid">
      {fields.map((field) => (
        <div className="passport-field" key={field.key}>
          <dt>{field.label}</dt>
          <dd>{displayValue(field.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Timeline({ items, emptyText, type = "maintenance" }) {
  if (!items.length) {
    return <div className="passport-empty">{emptyText}</div>;
  }

  return (
    <div className="passport-timeline">
      {items.map((item, index) => {
        const user = item.inspector_user || item.author_user;
        const author = user
          ? `${user.lastname || ""} ${user.name || ""}`.trim()
          : null;

        return (
          <article className="passport-timeline__item" key={item.id || index}>
            <div className="passport-timeline__rail" />
            <div className="passport-timeline__body">
              <div className="passport-timeline__top">
                <span className={`passport-chip passport-chip--${type}`}>
                  {formatDate(item.date || item.event_date || item.date_created)}
                </span>
                {author && <span className="passport-timeline__author">{author}</span>}
              </div>
              <h3>{item.topic || item.protection_type || "Ёзув"}</h3>
              {item.section && <p>Участка: {item.section}</p>}
              {item.repair_date && <p>Ремонт вақти: {formatDate(item.repair_date)}</p>}
              {item.recorder_reading && <p>Қайд этувчи мослама: {item.recorder_reading}</p>}
              {item.cause && <p>Сабаб: {item.cause}</p>}
              {item.measures && <p>Чоралар: {item.measures}</p>}
              {item.result && <p>Натижа: {item.result}</p>}
              {item.comment && <p>{item.comment}</p>}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function PolesTable({ poles, slug, loading, error }) {
  if (loading) return <div className="passport-empty">Таянчлар юкланмоқда.</div>;
  if (error) return <div className="passport-empty passport-empty--error">{error}</div>;
  if (!poles.length) return <div className="passport-empty">Таянчлар бўйича маълумот йўқ.</div>;

  return (
    <div className="passport-table-wrap">
      <table className="passport-table">
        <thead>
          <tr>
            <th>Таянч</th>
            <th>Координата</th>
            <th>Тартиб</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {poles.map((pole) => (
            <tr key={pole.id}>
              <td>
                <strong>№{displayValue(pole.name)}</strong>
              </td>
              <td>
                {displayValue(pole.latitude)}, {displayValue(pole.longitude)}
              </td>
              <td>{displayValue(pole.order_index)}</td>
              <td>
                <Link
                  className="passport-table-link"
                  to={`/indicators/networks/${encodeURIComponent(slug)}/poles/${pole.id}`}
                >
                  Паспорт
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const NetPassport = () => {
  const { slug } = useParams();
  const [network, setNetwork] = useState(null);
  const [poles, setPoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polesLoading, setPolesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [polesError, setPolesError] = useState(null);
  const [activeHistory, setActiveHistory] = useState("inspection");

  useEffect(() => {
    let cancelled = false;

    const fetchNetwork = async () => {
      setLoading(true);
      setError(null);
      setPoles([]);
      setPolesError(null);
      try {
        const res = await apiFetch(`/api/network/${slug}`, { cache: "no-store" });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || "Линия не найдена");
        }

        const data = await res.json();
        if (cancelled) return;
        setNetwork(data);

        if (data?.id) {
          setPolesLoading(true);
          try {
            const polesRes = await apiFetch(`/api/poles-lite?network_id=${encodeURIComponent(data.id)}`, {
              cache: "no-store",
            });
            if (!polesRes.ok) {
              const text = await polesRes.text();
              throw new Error(text || "Не удалось загрузить опоры");
            }
            const poleItems = await polesRes.json();
            if (!cancelled) setPoles(Array.isArray(poleItems) ? poleItems : []);
          } catch (e) {
            if (!cancelled) setPolesError(e.message || "Ошибка загрузки опор");
          } finally {
            if (!cancelled) setPolesLoading(false);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setNetwork(null);
          setError(e.message || "Ошибка загрузки линии");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (slug) fetchNetwork();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const view = useMemo(() => {
    if (!network) return null;
    const inspections = sortByDateDesc(network.network_inspections);
    const accidents = sortByDateDesc(network.network_accidents);

    return {
      inspections,
      accidents,
      baseFields: [
        { key: "name", label: "ЭУҲТ диспетчерлик номланиши", value: network.name },
        { key: "city", label: "Балансда сақловчи МЭТ филиали", value: network.city?.name },
        { key: "voltage", label: "Кучланиши (кВ)", value: network.voltage },
        { key: "length", label: "Узунлиги (км)", value: network.length },
        { key: "start_year", label: "Ишга туширилган йили", value: network.start_year },
        { key: "span", label: "Бошланиш/охирги нуқталари", value: network.span },
        { key: "wire_type", label: "Сим тури", value: network.wire_type },
        { key: "wire_protection_type", label: "Чақмоқдан ҳимоя троси", value: network.wire_protection_type },
        { key: "optical_wire_type", label: "Оптик алоқа толали трос", value: network.optical_wire_type },
        { key: "poles_quantity", label: "Таянчлар сони", value: network.poles_quantity },
        { key: "intersections", label: "Кесишувлар", value: network.intersections },
      ],
    };
  }, [network]);

  if (loading) {
    return <PassportState title="ЭУҲТ паспорти юкланмоқда" text="Линия маълумотлари тайёрланмоқда." />;
  }

  if (error || !network || !view) {
    return (
      <PassportState
        title="Линия топилмади"
        text={error || "URL параметрларини текширинг."}
        tone="error"
      />
    );
  }

  const selectedHistory = activeHistory === "inspection" ? view.inspections : view.accidents;

  return (
    <Layout>
      <div className="container">
        <main className="passport-page passport-page--network">
          <section className="passport-hero passport-hero--network">
            <div className="passport-breadcrumbs">
              <Link to="/">Бош саҳифа</Link>
              <span>/</span>
              <Link to="/indicators/networks">Электр узатиш тармоқлари</Link>
              <span>/</span>
              <span>{network.name}</span>
            </div>

            <div className="passport-hero__grid">
              <div>
                <span className="passport-kicker">ЭУҲТ паспорти</span>
                <h1>{network.name || "Электр узатиш тармоғи"}</h1>
                <p>
                  {network.city?.name || DASH} филиали, {displayValue(network.voltage)} кВ линия.
                </p>
              </div>

              <aside className="passport-identity">
                <span className="passport-identity__label">Таянчлар</span>
                <strong>{poles.length || displayValue(network.poles_quantity)}</strong>
                <span>{displayValue(network.length)} км</span>
              </aside>
            </div>
          </section>

          <section className="passport-metrics">
            <div className="passport-metric">
              <span>Филиал</span>
              <strong>{network.city?.name || DASH}</strong>
            </div>
            <div className="passport-metric">
              <span>Кучланиш</span>
              <strong>{displayValue(network.voltage)}</strong>
            </div>
            <div className="passport-metric">
              <span>Узунлик</span>
              <strong>{displayValue(network.length)}</strong>
            </div>
            <div className="passport-metric">
              <span>Журналлар</span>
              <strong>{view.inspections.length + view.accidents.length}</strong>
            </div>
          </section>

          <div className="passport-layout">
            <div className="passport-main">
              <section className="passport-card">
                <div className="passport-card__head">
                  <span className="passport-section-number">01</span>
                  <div>
                    <h2>Техник маълумотлар</h2>
                    <p>Линия паспортининг асосий кўрсаткичлари.</p>
                  </div>
                </div>
                <FieldGrid fields={view.baseFields} />
              </section>

              <section className="passport-card">
                <div className="passport-card__head">
                  <span className="passport-section-number">02</span>
                  <div>
                    <h2>Таянчлар</h2>
                    <p>Линияга боғланган таянчлар рўйхати.</p>
                  </div>
                </div>
                <PolesTable
                  poles={poles}
                  slug={slug}
                  loading={polesLoading}
                  error={polesError}
                />
              </section>
            </div>

            <aside className="passport-side">
              <section className="passport-card passport-card--sticky">
                <div className="passport-card__head passport-card__head--compact">
                  <span className="passport-section-number">03</span>
                  <div>
                    <h2>Эксплуатация</h2>
                    <p>Осмотрлар, таъмир ва авариялар журнали.</p>
                  </div>
                </div>

                <div className="passport-segment">
                  <button
                    type="button"
                    className={activeHistory === "inspection" ? "passport-segment__btn passport-segment__btn--active" : "passport-segment__btn"}
                    onClick={() => setActiveHistory("inspection")}
                  >
                    Эксплуатация
                  </button>
                  <button
                    type="button"
                    className={activeHistory === "accident" ? "passport-segment__btn passport-segment__btn--active" : "passport-segment__btn"}
                    onClick={() => setActiveHistory("accident")}
                  >
                    Авариялар
                  </button>
                </div>

                <Timeline
                  items={selectedHistory}
                  emptyText={
                    activeHistory === "inspection"
                      ? "Эксплуатация бўйича маълумот йўқ."
                      : "Авариялар бўйича маълумот йўқ."
                  }
                  type={activeHistory === "inspection" ? "maintenance" : "accident"}
                />
              </section>
            </aside>
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default NetPassport;
